import type { FilterGroup, FilterSelection, ThemeMode } from "@carbon6/c6-frontend-design";
import type { DatasetAnalyticsResp } from "@/api";
import type { LabelConditionOption } from "./datasetFilters";
import { facetChipColor } from "./facetChipColors";
import type { FilterSearchCandidate } from "./FilterSearchCombobox";

/**
 * 검색 콤보박스의 후보 목록이다. facet 축 값과 generic 라벨을 한 목록으로 합쳐 **데이터셋 수
 * 내림차순**으로 정렬한다. 수가 같으면 이름순이고, 수를 모르는 후보는 뒤에 이름순으로 둔다.
 *
 * 건수의 출처는 두 가지다:
 * - facet 축(작업·환경·데이터 소스·Embodiment·Tip·수집 방식·팔 구성·카메라 수): 조직 전체
 *   통계(`datasetAnalyticsStore`)의 값별 데이터셋 수
 * - generic 라벨: 라벨 카탈로그의 `dataset_count`
 * 둘 다 available 데이터셋 수라서 같은 기준으로 비교할 수 있다. 통계가 아직 없으면 facet 후보는
 * 수 없이 뒤에 두고, 통계가 도착하면 다시 정렬한다.
 *
 * 후보 `value` 는 `key:value` 형식 하나다. facet 키와 generic 키가 겹치지 않으므로 유일하다.
 * 고른 뒤 어느 축으로 보낼지는 키가 정한다(`DatasetFilterController.selectLabelCandidate`).
 */
export type LabelRecommendationSources = {
  groups: readonly FilterGroup[];
  labelOptions: readonly LabelConditionOption[];
  // 카탈로그 라벨 색(`key:value` → 0xRRGGBB)이다. 예약 라벨 facet(작업·환경·소스·Quality)의 값도
  // 라벨이라 색이 있다. 라벨이 아닌 축은 테마별 facet 팔레트(facetChipColors.ts)로 그린다.
  labelColors?: ReadonlyMap<string, number>;
  analytics: DatasetAnalyticsResp | null;
  // 라벨이 아닌 축의 칩 색을 고를 테마다. 다크는 밝은 단계, 라이트는 어두운 단계를 쓴다.
  themeMode?: ThemeMode;
};

export function buildLabelRecommendations({
  groups,
  labelOptions,
  labelColors,
  analytics,
  themeMode = "light",
}: LabelRecommendationSources): readonly FilterSearchCandidate[] {
  const counts = analyticsCountIndex(analytics);
  const candidates: FilterSearchCandidate[] = [];
  for (const group of groups) {
    for (const option of group.filters) {
      const value = `${group.value}:${option.value}`;
      const count = counts.get(group.value)?.get(option.value);
      candidates.push({
        value,
        label: `${group.label}: ${option.label}`,
        description: option.description ?? group.label,
        color: labelColors?.get(value) ?? facetChipColor(group.value, themeMode),
        ...(count !== undefined ? { count } : {}),
      });
    }
  }
  for (const option of labelOptions) {
    candidates.push({
      value: option.value,
      label: option.label,
      color: option.color,
      ...(option.description !== undefined ? { description: option.description } : {}),
      ...(option.accentColor !== undefined ? { accentColor: option.accentColor } : {}),
      count: option.count,
    });
  }
  return candidates.sort(compareByCountDesc);
}

/** 이미 적용한 조건의 후보 값 집합이다. facet 선택과 라벨 조건을 같은 `key:value` 형식으로 모은다. */
export function appliedCandidateValues(
  selection: FilterSelection,
  labelTokenValues: readonly string[],
): ReadonlySet<string> {
  const applied = new Set<string>(labelTokenValues);
  for (const [key, values] of Object.entries(selection)) {
    for (const value of values) {
      applied.add(`${key}:${value}`);
    }
  }
  return applied;
}

function compareByCountDesc(left: FilterSearchCandidate, right: FilterSearchCandidate): number {
  const leftCount = left.count ?? -1;
  const rightCount = right.count ?? -1;
  if (leftCount !== rightCount) {
    return rightCount - leftCount;
  }
  return left.label.localeCompare(right.label);
}

// facet 키 → (값 → 데이터셋 수)다. 통계 응답의 축 이름과 facet 키가 다른 경우는 여기서만 연결한다.
// `value: null`(미판정 묶음)은 고를 수 있는 값이 아니므로 뺀다.
function analyticsCountIndex(
  analytics: DatasetAnalyticsResp | null,
): ReadonlyMap<string, ReadonlyMap<string, number>> {
  const index = new Map<string, Map<string, number>>();
  if (analytics === null) {
    return index;
  }
  const axes: readonly [string, readonly { value: string | number | null; count: number }[]][] = [
    ["task", analytics.labels.task],
    ["environment", analytics.labels.environment],
    ["source", analytics.labels.source],
    ["embodiment", analytics.embodiment_counts],
    ["tip", analytics.tip_counts],
    ["recording_mode", analytics.recording_mode_counts],
    ["arms", analytics.arm_layout_counts],
    ["camera_count", analytics.camera_stream_counts],
  ];
  for (const [key, rows] of axes) {
    const byValue = new Map<string, number>();
    for (const row of rows) {
      if (row.value !== null) {
        byValue.set(String(row.value), row.count);
      }
    }
    index.set(key, byValue);
  }
  return index;
}
